import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crosshair, ArrowRight, ArrowLeft, X } from "lucide-react";

const TOUR_FLAG = "os_show_tour";
// The tour anchors to in-app nav elements; never show it over the auth/entry routes.
const SUPPRESSED_ROUTES = ["/", "/join", "/login", "/register"];

const STEPS = [
  { selector: '[data-testid="nav-create"]', title: "Post a listing", body: "List a scope, red dot or optic in under a minute — your first 25 each month are free." },
  { selector: '[data-testid="feed-search"]', title: "Search & filter", body: "Hunt by brand, reticle, magnification, price and location to find exactly the gear you want." },
  { selector: '[data-testid="nav-messages"]', title: "Message sellers", body: "Negotiate and arrange deals right here — no phone numbers required." },
  { selector: '[data-testid="nav-feedback"]', title: "Tell us anything", body: "You're an early tester. Tap the feedback icon in the header any time to send a bug, idea or gripe." },
];

export default function WelcomeTour() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (user && localStorage.getItem(TOUR_FLAG) === "1") {
      const t = setTimeout(() => setActive(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  const measure = useCallback(() => {
    const el = document.querySelector(STEPS[step].selector);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!active) return;
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, step, measure]);

  const finish = async () => {
    setActive(false);
    localStorage.removeItem(TOUR_FLAG);
    try { await api.post("/me/tour-seen"); } catch {}
  };

  if (!active || SUPPRESSED_ROUTES.includes(pathname)) return null;

  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  // Position the card: below the target if there's room, else above/centered.
  let cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  if (rect) {
    const belowSpace = window.innerHeight - (rect.top + rect.height);
    const cardW = 300;
    let left = rect.left + rect.width / 2 - cardW / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - cardW - 12));
    if (belowSpace > 190) {
      cardStyle = { top: rect.top + rect.height + 14, left, width: cardW, transform: "none" };
    } else {
      cardStyle = { top: Math.max(12, rect.top - 190), left, width: cardW, transform: "none" };
    }
  }

  return (
    <div className="fixed inset-0 z-[70]" data-testid="welcome-tour">
      <div className="absolute inset-0 bg-black/70" onClick={finish} />
      {rect && (
        <div
          className="absolute border-2 border-volt pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      )}
      <div
        className="absolute bg-surface border border-sniper p-5 shadow-2xl shadow-black/60 animate-fade-up"
        style={cardStyle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-volt">
            <Crosshair className="w-4 h-4" strokeWidth={1.8} />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Step {step + 1}/{STEPS.length}</span>
          </div>
          <button onClick={finish} data-testid="tour-skip" className="text-secondary-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="font-heading text-xl font-bold uppercase tracking-tight mt-3 text-foreground">{s.title}</h3>
        <p className="text-sm font-body text-secondary-foreground mt-1.5 leading-relaxed">{s.body}</p>
        <div className="flex items-center justify-between mt-5">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} data-testid="tour-back" className="inline-flex items-center gap-1 text-xs font-mono uppercase text-secondary-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          ) : (
            <button onClick={finish} data-testid="tour-skip-text" className="text-xs font-mono uppercase text-muted-foreground hover:text-foreground transition-colors">Skip tour</button>
          )}
          <button
            onClick={() => (last ? finish() : setStep(step + 1))}
            data-testid="tour-next"
            className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors"
          >
            {last ? "Get started" : "Next"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
